import React from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import FooterSection from "../components/FooterSection";
import PageSEO from "../components/PageSEO";
import "../styles/PaidSocialService.css";

export default function PaidSocialPage() {
  return (
    <div className="paid-social-page">
      <PageSEO
        title="Paid Social Services | Meta, LinkedIn & TikTok Ads | Sparkdenz"
        description="Scale social advertising with Sparkdenz paid social services across Meta, LinkedIn, and TikTok with creative testing, funnel optimization, and attribution reporting."
        canonicalPath="/paid-social"
      />
      <MenuBar />
      <main>
        {/* Hero Section */}
        <section className="service-hero">
          <div className="section-inner">
            <p className="service-tag">Paid Social Advertising</p>
            <h1>Build Genuine Engagement and Drive Real Results on Social</h1>
            <p className="service-hero-text">
              Reach your audience where they spend their time. Our data-driven social advertising 
              campaigns on Meta, LinkedIn, and TikTok create authentic connections that convert 
              into customers and brand advocates.
            </p>
            <div className="service-hero-actions">
              <Link className="btn primary" to="/contact-us">
                Get Your Free Strategy
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Schedule Call
              </Link>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="service-details">
          <div className="section-inner">
            <h2>Paid Social Services</h2>
            <div className="services-grid">
              <div className="service-item">
                <div className="service-icon">👥</div>
                <h3>Meta Ads (Facebook & Instagram)</h3>
                <p>
                  Leverage Meta's powerful targeting and creative tools to reach your ideal 
                  audience. From awareness to conversion, we build cohesive campaigns that 
                  maximize engagement and ROI on the world's largest social platform.
                </p>
                <ul className="service-list">
                  <li>Campaign strategy & setup</li>
                  <li>Audience segmentation</li>
                  <li>Creative optimization</li>
                  <li>Retargeting campaigns</li>
                  <li>Performance monitoring</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">💼</div>
                <h3>LinkedIn Advertising</h3>
                <p>
                  Dominate B2B social with LinkedIn ads. We target decision-makers and professionals 
                  with precision, building demand generation and lead acquisition campaigns that 
                  drive qualified business opportunities.
                </p>
                <ul className="service-list">
                  <li>B2B audience targeting</li>
                  <li>Lead generation forms</li>
                  <li>Account-based campaigns</li>
                  <li>Sponsored content</li>
                  <li>InMail campaigns</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🎬</div>
                <h3>TikTok & Emerging Platforms</h3>
                <p>
                  Don't miss Gen Z and younger audiences. We create native, authentic content 
                  for TikTok and other emerging platforms that feels organic while driving 
                  measurable brand awareness and sales.
                </p>
                <ul className="service-list">
                  <li>TikTok Ads management</li>
                  <li>Creator collaboration</li>
                  <li>Trend-based campaigns</li>
                  <li>Platform experimentation</li>
                  <li>Viral content strategy</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🎨</div>
                <h3>Creative Development</h3>
                <p>
                  Great creative is non-negotiable. We develop, test, and iterate on compelling 
                  video and image assets that stop scrolls, spark action, and drive conversions 
                  across all social platforms.
                </p>
                <ul className="service-list">
                  <li>Video content creation</li>
                  <li>Image design & optimization</li>
                  <li>Copy writing</li>
                  <li>A/B creative testing</li>
                  <li>UGC content strategy</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🔄</div>
                <h3>Funnel & Conversion Optimization</h3>
                <p>
                  Traffic without conversions is wasted budget. We optimize every step of your 
                  social funnel, from awareness ads to landing pages, ensuring maximum conversion 
                  rates and customer lifetime value.
                </p>
                <ul className="service-list">
                  <li>Landing page optimization</li>
                  <li>Conversion funnel design</li>
                  <li>Checkout optimization</li>
                  <li>Lead nurture sequences</li>
                  <li>Sales enablement</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📊</div>
                <h3>Analytics & Attribution</h3>
                <p>
                  We track what matters and cut through the noise. Custom dashboards and 
                  attribution models show the true impact of your social spend on revenue 
                  and business objectives.
                </p>
                <ul className="service-list">
                  <li>Conversion tracking</li>
                  <li>Attribution modeling</li>
                  <li>Custom dashboards</li>
                  <li>ROI measurement</li>
                  <li>Monthly reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="service-results">
          <div className="section-inner">
            <h2>Proven Performance</h2>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-stat">285%</div>
                <p>Average ROAS improvement</p>
              </div>
              <div className="result-card">
                <div className="result-stat">58%</div>
                <p>Average CPC reduction</p>
              </div>
              <div className="result-card">
                <div className="result-stat">4.2x</div>
                <p>Average ROAS achieved</p>
              </div>
              <div className="result-card">
                <div className="result-stat">203%</div>
                <p>Average lead increase</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="section-inner">
            <h2>Our Paid Social Approach</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">01</div>
                <h3>Strategy & Audit</h3>
                <p>Analyze your audience, competitors, and goals to build a comprehensive paid social strategy.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">02</div>
                <h3>Creative & Messaging</h3>
                <p>Develop compelling creative and messaging that resonates with your target audience.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">03</div>
                <h3>Campaign Launch</h3>
                <p>Execute campaigns with proper tracking, audience setup, and bid optimization.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">04</div>
                <h3>Testing & Optimization</h3>
                <p>Continuous creative testing and performance optimization for maximum ROI.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="section-inner">
            <h2>Ready to Dominate Social Advertising?</h2>
            <p>Let's build campaigns that create genuine engagement and drive measurable business results.</p>
            <div className="cta-buttons">
              <Link className="btn primary" to="/contact-us">
                Get Your Strategy
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Talk to Expert
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
